import { useCallback, useMemo } from 'react';
import {
  Baseline,
  IElement,
  OneOfElements,
} from '@shared/models/project.model';
import {
  getSelectedVersion,
  useSelectedVersionProducts,
} from '@client/store/ui';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import {
  generateElementFallbackName,
  getFallbackName,
} from '@shared/helpers/project.helpers';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { getProject, useUpdateProject } from '@client/store/project';
import { isBuildingVersionElement } from '@shared/helpers/recursive-element.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_NAME_UPDATE } from '@shared/constants';
import { OneOfFactoryElements } from '@shared/models/factory-element.model';
import { getRecipeLookup } from '@client/store/recipe/recipe.hook';

type ElementFallbackName = (
  /**
   * Name to set on the elements.
   * undefined means that the fallback should be regenerated if it exists.
   */
  name: string | undefined,
  ...elements: (IElement | Baseline)[]
) => Promise<void>;

/**
 * Set name on one or more elements.
 * Pass undefined as name to regenerate fallback name.
 * @returns
 */
export const useSetNameOfElements = (): ElementFallbackName => {
  const updateProject = useUpdateProject();
  const generateFallbackName = useGenerateFallbackName();
  const selectedProposalId = useSelectedProposalId();

  return useCallback(
    async (name, ...elements) => {
      if (!elements.length) return;

      const changes: ProjectChange[] = elements.map((element) => {
        const id = element.id;

        if (isBuildingVersionElement(element)) {
          if (!name) {
            throw new Error('Cannot set empty name on building version');
          }
          return {
            type: ProjectChangeType.VersionName,
            versionId: id,
            name,
          };
        }

        return {
          type: ProjectChangeType.ElementName,
          elementId: id,
          name: name ?? '',
          fallbackName: generateFallbackName(element),
        };
      });

      await updateProject({
        kind: 'changes',
        baseline: getProject(),
        changes,
        selectedProposalId,
      });

      for (const element of elements) {
        amplitudeLog(AMP_ELEMENT_NAME_UPDATE, {
          Name: name,
          ElementID: element.id,
        });
      }
    },
    [generateFallbackName, selectedProposalId, updateProject],
  );
};

export const useElementName = (
  element: OneOfElements | undefined,
  defaultName = 'Unknown',
): string => {
  const products = useSelectedVersionProducts();

  return useMemo(
    () => getElementName(element, products, defaultName),
    [defaultName, element, products],
  );
};

/**
 * Get a function that generates a new fallback name.
 * @returns
 */
export const useGenerateFallbackName = () => {
  return useCallback(
    (
      element?: OneOfElements | OneOfFactoryElements,
      forceRegeneration?: boolean,
    ): string => {
      return generateElementFallbackName(
        getSelectedVersion(true),
        element,
        getRecipeLookup(),
        forceRegeneration,
      );
    },
    [],
  );
};

/**
 * Apply a new fallback name to an element without updating on the server.
 * @returns
 */
export const useApplyFallbackName = () => {
  const generateFallbackName = useGenerateFallbackName();

  return useCallback(
    (element: IElement): IElement => {
      const fallbackName = generateFallbackName(element);

      // Only update if the fallback name has changed
      return getFallbackName(element) !== fallbackName
        ? { ...element, fallbackName }
        : element;
    },
    [generateFallbackName],
  );
};
